/**
 * Global Search API - Combines search across projects, tasks, and subtasks
 */
import axiosClient from './axiosClient';
import { Project, Task, Subtask } from '@/types';

export interface GlobalSearchResult {
    projects: Project[];
    tasks: Task[];
    subtasks: Subtask[];
    taskTotal: number;
}

interface GlobalSearchOptions {
    userId?: string;
    taskLimit?: number;
    organization_id?:string;
}

const DEFAULT_TASK_LIMIT = 20;
const MAX_TASK_LIMIT = 500;

export const searchAPI = {
    /**
     * Global search across projects, tasks, and subtasks
     * @param query - Search query string
     * @param options - userId for filtering assigned items, taskLimit to control page size
     * @returns Combined search results
     */
    globalSearch: async (query: string, options: GlobalSearchOptions = {}): Promise<GlobalSearchResult> => {
        const { userId, taskLimit, organization_id } = options;
        const effectiveTaskLimit = Math.min(Math.max(taskLimit ?? DEFAULT_TASK_LIMIT, 1), MAX_TASK_LIMIT);
        if (!query || !query.trim()) {
            return { projects: [], tasks: [], subtasks: [], taskTotal: 0 };
        }
    
        try {
            const queryLower = query.toLowerCase().trim();
    
            const projectsRes = await axiosClient.get<Project[]>('/projects', {
                params: { is_active: true, limit: 50, organization_id }
            });
    
            const projects = projectsRes.data.filter(p =>
                p.name?.toLowerCase().includes(queryLower) ||
                p.key?.toLowerCase().includes(queryLower) ||
                p.description?.toLowerCase().includes(queryLower)
            );
    
            const allOrgProjects = projectsRes.data;
            const orgProjectIds = allOrgProjects.map(p => p.id).filter(Boolean);
    
            if (orgProjectIds.length === 0) {
                return { projects, tasks: [], subtasks: [], taskTotal: 0 };
            }

            const tasksRes = await axiosClient.get('/tasks/search', {
                params: {
                    query: query.trim(),
                    limit: effectiveTaskLimit,
                    project_ids: orgProjectIds.join(',')
                }
            });
    
            const tasksPayload = (tasksRes.data as any);
            const tasks = Array.isArray(tasksPayload) ? tasksPayload : (tasksPayload.items || []);
            const taskTotal = typeof tasksPayload.total === 'number' ? tasksPayload.total : tasks.length;
    
            const orgTaskIds = tasks.map((t: any) => t.id).filter(Boolean);
    
            if (orgTaskIds.length === 0) {
                return { projects, tasks, subtasks: [], taskTotal };
            }

            const subtasksRes = await axiosClient.get<Subtask[]>('/subtasks/all-subtasks', {
                params: {
                    task_ids: orgTaskIds.join(','),
                    ...(userId ? { assigned_to: userId } : {}),
                    limit: 50
                }
            });
    
            const subtasks = subtasksRes.data.filter(s =>
                s.title?.toLowerCase().includes(queryLower) ||
                s.id?.toLowerCase().includes(queryLower) ||
                s.description?.toLowerCase().includes(queryLower)
            );
    
            return { projects, tasks, subtasks, taskTotal };
    
        } catch (error) {
            console.error('[GlobalSearch] Error during search:', error);
            return { projects: [], tasks: [], subtasks: [], taskTotal: 0 };
        }
    }
};
